/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { GpsProvider, PositionReportSource } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface VehiclePositionSummaryProps {
  id: string;
  vehicleId: string;
  latitude: number;
  longitude: number;
  positionSource: PositionReportSource;
  positionTime: Date;
  lastPositionReportId: string | null;
  lastQualifyingPositionReportTime: Date | null;
  etlo: Date | null;
  lastGpsAutoPositionTime: Date | null;
  lastTakeOffTime: Date | null;
  lastLandedTime: Date | null;
  gpsProvider: GpsProvider | null;
  altitude: number | null;
  speedInKnots: number | null;
  heading: number | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
}

export class VehiclePositionSummaryRecord extends Record<VehiclePositionSummaryProps>({
  id: null as any,
  vehicleId: null as any,
  latitude: null as any,
  longitude: null as any,
  positionSource: null as any,
  positionTime: null as any,
  lastPositionReportId: null as any,
  lastQualifyingPositionReportTime: null as any,
  etlo: null as any,
  lastGpsAutoPositionTime: null as any,
  lastTakeOffTime: null as any,
  lastLandedTime: null as any,
  gpsProvider: null as any,
  altitude: null as any,
  speedInKnots: null as any,
  heading: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
}) {
  public static fromJS(json: {
    id: string;
    vehicleId: string;
    latitude: number;
    longitude: number;
    positionSource: PositionReportSource;
    positionTime: string | number | Date;
    lastPositionReportId: string | null;
    lastQualifyingPositionReportTime: string | number | Date | null;
    etlo: string | number | Date | null;
    lastGpsAutoPositionTime: string | number | Date | null;
    lastTakeOffTime: string | number | Date | null;
    lastLandedTime: string | number | Date | null;
    gpsProvider: GpsProvider | null;
    altitude: number | null;
    speedInKnots: number | null;
    heading: number | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
  }): VehiclePositionSummaryRecord {
    if (json == null) {
      throw new Error('VehiclePositionSummaryRecord.fromJS: parameter cannot be null');
    }
    return new VehiclePositionSummaryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'VehiclePositionSummaryJson.id',
      ),
      vehicleId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'vehicleId', '00000000-0000-0000-0000-000000000000'),
        'VehiclePositionSummaryJson.vehicleId',
      ),
      latitude: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'latitude', 0.0),
        'VehiclePositionSummaryJson.latitude',
      ),
      longitude: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'longitude', 0.0),
        'VehiclePositionSummaryJson.longitude',
      ),
      positionSource: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'positionSource', PositionReportSource.Pilot),
        'VehiclePositionSummaryJson.positionSource',
      ),
      positionTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'positionTime', '0001-01-01T00:00:00.00Z'),
          'VehiclePositionSummaryJson.positionTime',
        ),
      ),
      lastPositionReportId: json.lastPositionReportId,
      lastQualifyingPositionReportTime:
        json.lastQualifyingPositionReportTime != null
          ? new Date(json.lastQualifyingPositionReportTime)
          : null,
      etlo: json.etlo != null ? new Date(json.etlo) : null,
      lastGpsAutoPositionTime:
        json.lastGpsAutoPositionTime != null ? new Date(json.lastGpsAutoPositionTime) : null,
      lastTakeOffTime: json.lastTakeOffTime != null ? new Date(json.lastTakeOffTime) : null,
      lastLandedTime: json.lastLandedTime != null ? new Date(json.lastLandedTime) : null,
      gpsProvider: json.gpsProvider,
      altitude: json.altitude,
      speedInKnots: json.speedInKnots,
      heading: json.heading,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'VehiclePositionSummaryJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    vehicleId: string;
    latitude: number;
    longitude: number;
    positionSource: PositionReportSource;
    positionTime: string | number | Date;
    lastPositionReportId: string | null;
    lastQualifyingPositionReportTime: string | number | Date | null;
    etlo: string | number | Date | null;
    lastGpsAutoPositionTime: string | number | Date | null;
    lastTakeOffTime: string | number | Date | null;
    lastLandedTime: string | number | Date | null;
    gpsProvider: GpsProvider | null;
    altitude: number | null;
    speedInKnots: number | null;
    heading: number | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
  }): [VehiclePositionSummaryRecord, Error[]] {
    if (json == null) {
      throw new Error('VehiclePositionSummaryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new VehiclePositionSummaryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'VehiclePositionSummaryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'vehicleId', '00000000-0000-0000-0000-000000000000'),
            'VehiclePositionSummaryJson.vehicleId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      latitude: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'latitude', 0.0),
            'VehiclePositionSummaryJson.latitude',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      longitude: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'longitude', 0.0),
            'VehiclePositionSummaryJson.longitude',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      positionSource: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'positionSource', PositionReportSource.Pilot),
            'VehiclePositionSummaryJson.positionSource',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      positionTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'positionTime', '0001-01-01T00:00:00.00Z'),
              'VehiclePositionSummaryJson.positionTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastPositionReportId: tryOrDefault(
        () => json.lastPositionReportId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastQualifyingPositionReportTime: tryOrDefault(
        () =>
          json.lastQualifyingPositionReportTime != null
            ? new Date(json.lastQualifyingPositionReportTime)
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      etlo: tryOrDefault(
        () => (json.etlo != null ? new Date(json.etlo) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastGpsAutoPositionTime: tryOrDefault(
        () =>
          json.lastGpsAutoPositionTime != null ? new Date(json.lastGpsAutoPositionTime) : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastTakeOffTime: tryOrDefault(
        () => (json.lastTakeOffTime != null ? new Date(json.lastTakeOffTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastLandedTime: tryOrDefault(
        () => (json.lastLandedTime != null ? new Date(json.lastLandedTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      gpsProvider: tryOrDefault(
        () => json.gpsProvider,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      altitude: tryOrDefault(
        () => json.altitude,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      speedInKnots: tryOrDefault(
        () => json.speedInKnots,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      heading: tryOrDefault(
        () => json.heading,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'VehiclePositionSummaryJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
    });

    return [record, errors];
  }

  constructor(props: VehiclePositionSummaryProps) {
    super(props);
    if (props == null) {
      throw new Error('VehiclePositionSummaryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class VehiclePositionSummaryViewModelBase implements VehiclePositionSummaryProps {
  protected readonly _props: VehiclePositionSummaryProps;
  private _json?: VehiclePositionSummaryProps;

  get id(): string {
    return this._props.id;
  }
  get vehicleId(): string {
    return this._props.vehicleId;
  }
  get latitude(): number {
    return this._props.latitude;
  }
  get longitude(): number {
    return this._props.longitude;
  }
  get positionSource(): PositionReportSource {
    return this._props.positionSource;
  }
  get positionTime(): Date {
    return this._props.positionTime;
  }
  get lastPositionReportId(): string | null {
    return this._props.lastPositionReportId;
  }
  get lastQualifyingPositionReportTime(): Date | null {
    return this._props.lastQualifyingPositionReportTime;
  }
  get etlo(): Date | null {
    return this._props.etlo;
  }
  get lastGpsAutoPositionTime(): Date | null {
    return this._props.lastGpsAutoPositionTime;
  }
  get lastTakeOffTime(): Date | null {
    return this._props.lastTakeOffTime;
  }
  get lastLandedTime(): Date | null {
    return this._props.lastLandedTime;
  }
  get gpsProvider(): GpsProvider | null {
    return this._props.gpsProvider;
  }
  get altitude(): number | null {
    return this._props.altitude;
  }
  get speedInKnots(): number | null {
    return this._props.speedInKnots;
  }
  get heading(): number | null {
    return this._props.heading;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }

  public get isGpsAuto() {
    return this._props.positionSource === PositionReportSource.GpsAuto;
  }
  public get isGpsAutoAdsb() {
    return this._props.positionSource === PositionReportSource.GpsAutoAdsb;
  }
  public get isGpsManual() {
    return this._props.positionSource === PositionReportSource.GpsManual;
  }
  public get isPilot() {
    return this._props.positionSource === PositionReportSource.Pilot;
  }
  public get isTCS() {
    return this._props.positionSource === PositionReportSource.TCS;
  }
  public get isMediationService() {
    return this._props.gpsProvider === GpsProvider.MediationService;
  }
  public get isSkytrac() {
    return this._props.gpsProvider === GpsProvider.Skytrac;
  }

  public isAnyPositionSource(...positionReportSource: PositionReportSource[]) {
    return positionReportSource.includes(this._props.positionSource);
  }
  public isAnyGpsProvider(...gpsProvider: GpsProvider[]) {
    return this._props.gpsProvider != null && gpsProvider.includes(this._props.gpsProvider);
  }

  constructor(props: VehiclePositionSummaryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        vehicleId: this._props.vehicleId,
        latitude: this._props.latitude,
        longitude: this._props.longitude,
        positionSource: this._props.positionSource,
        positionTime: this._props.positionTime,
        lastPositionReportId: this._props.lastPositionReportId,
        lastQualifyingPositionReportTime: this._props.lastQualifyingPositionReportTime,
        etlo: this._props.etlo,
        lastGpsAutoPositionTime: this._props.lastGpsAutoPositionTime,
        lastTakeOffTime: this._props.lastTakeOffTime,
        lastLandedTime: this._props.lastLandedTime,
        gpsProvider: this._props.gpsProvider,
        altitude: this._props.altitude,
        speedInKnots: this._props.speedInKnots,
        heading: this._props.heading,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllVehiclePositionSummaryEntities = (state: AppState) =>
  state.entityStores.vehiclePositionSummary.entities;

export const selectVehiclePositionSummaryById = (id: string) => (state: AppState) =>
  state.entityStores.vehiclePositionSummary.entities.get(id);

export const selectVehiclePositionSummaryMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.vehiclePositionSummary.meta.get(id);

export const selectVehiclePositionSummaryAndMetadataById = (id: string) =>
  createSelector(
    selectVehiclePositionSummaryById(id),
    selectVehiclePositionSummaryMetadataById(id),
    (vehiclePositionSummary, meta) => ({ vehiclePositionSummary, meta }),
  );

export function selectVehiclePositionSummaryEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllVehiclePositionSummaryEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, VehiclePositionSummaryRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectVehiclePositionSummaryEntitiesByVehicleId(vehicleId: string) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities.filter((e) => e.vehicleId === vehicleId).valueSeq(),
  );
}

export function selectVehiclePositionSummaryEntitiesByLatitude(latitude: number) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities.filter((e) => e.latitude === latitude).valueSeq(),
  );
}

export function selectVehiclePositionSummaryEntitiesByLongitude(longitude: number) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities.filter((e) => e.longitude === longitude).valueSeq(),
  );
}

export function selectVehiclePositionSummaryEntitiesByPositionSource(
  positionSource: PositionReportSource,
) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities.filter((e) => e.positionSource === positionSource).valueSeq(),
  );
}

export function selectVehiclePositionSummaryEntitiesByPositionTime(positionTime: Date) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities.filter((e) => e.positionTime === positionTime).valueSeq(),
  );
}

export function selectVehiclePositionSummaryEntitiesByLastPositionReportId(
  lastPositionReportId: string,
) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities.filter((e) => e.lastPositionReportId === lastPositionReportId).valueSeq(),
  );
}

export function selectVehiclePositionSummaryEntitiesByLastQualifyingPositionReportTime(
  lastQualifyingPositionReportTime: Date,
) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities
      .filter((e) => e.lastQualifyingPositionReportTime === lastQualifyingPositionReportTime)
      .valueSeq(),
  );
}

export function selectVehiclePositionSummaryEntitiesByEtlo(etlo: Date) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities.filter((e) => e.etlo === etlo).valueSeq(),
  );
}

export function selectVehiclePositionSummaryEntitiesByLastGpsAutoPositionTime(
  lastGpsAutoPositionTime: Date,
) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities.filter((e) => e.lastGpsAutoPositionTime === lastGpsAutoPositionTime).valueSeq(),
  );
}

export function selectVehiclePositionSummaryEntitiesByLastTakeOffTime(lastTakeOffTime: Date) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities.filter((e) => e.lastTakeOffTime === lastTakeOffTime).valueSeq(),
  );
}

export function selectVehiclePositionSummaryEntitiesByLastLandedTime(lastLandedTime: Date) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities.filter((e) => e.lastLandedTime === lastLandedTime).valueSeq(),
  );
}

export function selectVehiclePositionSummaryEntitiesByGpsProvider(gpsProvider: GpsProvider) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities.filter((e) => e.gpsProvider === gpsProvider).valueSeq(),
  );
}

export function selectVehiclePositionSummaryEntitiesByAltitude(altitude: number) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities.filter((e) => e.altitude === altitude).valueSeq(),
  );
}

export function selectVehiclePositionSummaryEntitiesBySpeedInKnots(speedInKnots: number) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities.filter((e) => e.speedInKnots === speedInKnots).valueSeq(),
  );
}

export function selectVehiclePositionSummaryEntitiesByHeading(heading: number) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities.filter((e) => e.heading === heading).valueSeq(),
  );
}

export function selectVehiclePositionSummaryEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllVehiclePositionSummaryEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
